


/**
 * Environment Configuration with Runtime Validation
 * 
 * This module validates environment variables at build/runtime to ensure
 * the application has all required configuration before it starts.
 * 
 * If validation fails, the application will throw a descriptive error
 * instead of failing silently with undefined values.
 */

import { z } from 'zod';

/**
 * Environment variables schema
 * Defines the structure and validation rules for all required env vars
 */
const envSchema = z.object({
  VITE_API_URL: z.string()
    .url('VITE_API_URL must be a valid URL')
    .min(1, 'VITE_API_URL is required'),
  
  VITE_IDENTITY_URL: z.string()
    .url('VITE_IDENTITY_URL must be a valid URL')
    .min(1, 'VITE_IDENTITY_URL is required'),

  VITE_NOTIFICATIONS_HUB_URL: z.string()
    .url('VITE_NOTIFICATIONS_HUB_URL must be a valid URL')
    .min(1, 'VITE_NOTIFICATIONS_HUB_URL is required'),
});

/**
 * Validate and parse environment variables
 * Throws a descriptive error if validation fails
 */
function validateEnv() {
  try {
    return envSchema.parse({
      VITE_API_URL: import.meta.env.VITE_API_URL,
      VITE_IDENTITY_URL: import.meta.env.VITE_IDENTITY_URL,
      VITE_NOTIFICATIONS_HUB_URL: import.meta.env.VITE_NOTIFICATIONS_HUB_URL,
    });
  } catch (error) {
    if (error instanceof z.ZodError) {
      const missingVars = error.errors.map(err => 
        `  - ${err.path.join('.')}: ${err.message}`
      ).join('\n');
      
      throw new Error(
        `❌ Invalid environment configuration:\n\n${missingVars}\n\n` +
        `Please check your .env file and ensure all required variables are set.\n` +
        `See .env.example for reference.`
      );
    }
    throw error;
  }
}

// Validate on module load
const env = validateEnv();

/**
 * Type-safe environment configuration
 * All values are guaranteed to be valid at this point
 */
export const ENV_CONFIG = {
  API_URL: env.VITE_API_URL,
  IDENTITY_URL: env.VITE_IDENTITY_URL,
  NOTIFICATIONS_HUB_URL: env.VITE_NOTIFICATIONS_HUB_URL,
} as const;

// Type export for TypeScript
export type EnvConfig = typeof ENV_CONFIG;
